import { type IReactionDisposer, makeAutoObservable, reaction } from 'mobx'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { logger, validPhoneNumber } from 'shared/lib'
import { LINK_ENABLE_MICROPHONE } from 'shared/constants/links'
import { toastStore } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { Contact } from 'entities/Contacts/model/Contact'
import { type IParamsCreateContact } from 'entities/Contacts/api/types'
import { CreateContactErrors } from 'entities/Subscription/ui/TrialContactsLimitModal'
import { FeatureRestrictedModalStore } from 'entities/UsersPermissions'
import { Inbox } from 'entities/Inbox/model/Inbox'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { Conversation } from 'entities/Conversation'
import { type IContactAll } from 'entities/Contacts'
import { showCallDuringQuietHoursModalIfNeeded } from 'entities/QuietHours'
import { CallModalActions } from 'widgets/CallModal/ui/CallModalActions'
import { CallModalContent } from 'widgets/CallModal/ui/CallModalContent'
import { SendFromStore } from 'widgets/SendFrom'

type ICallModalStoreConfig = {
  onSubmit?: (teamId: number) => void
  setHideTrialAlertNotForOwner?: () => void
}

export class CallModalStore {
  private _idModal = 'call_modal'
  private _loadingDeviceSettings = false
  private _contact: Contact | null = null
  private _contactId: number | null = null
  private _numberError = ''
  private _search = ''
  private _loading = false
  private _disposeSearch: IReactionDisposer | null = null
  private _config: ICallModalStoreConfig | null = null
  sendFromStore

  constructor(private _entities: IEntitiesStore, config?: ICallModalStoreConfig) {
    this.sendFromStore = new SendFromStore(this._entities, {
      getDropdownItemsOptions: {
        excludeShortCode: true,
        showOnlyVerifiedToolFree: true,
      },
      disabledPFT: true,
      personalInboxesOnly: true,
    })
    makeAutoObservable(this)

    this.setConfig(config)
  }

  setConfig = (config?: ICallModalStoreConfig) => {
    this._config = {
      ...this._config,
      ...config,
    }
  }

  openModal = () => {
    if (
      this._entities.inboxesStore.currentInbox instanceof Inbox &&
      !this._entities.inboxesStore.currentInbox?.isRCS
    ) {
      this.sendFromStore.setSelectItem({
        inbox_id: this._entities.inboxesStore.currentInbox?.id,
      })
    }

    modalStore.addModal({
      id: this._idModal,
      title: 'Make a call',
      showCloseButton: false,
      showCloseIcon: false,
      ModalActions: CallModalActions,
      ModalContent: CallModalContent,
      ModalContentProps: {
        callModalStore: this,
      },
      onClose: this.closeModal,
    })
  }

  closeModal = (reset = true) => {
    this._contact = null
    this._contactId = null
    this._search = ''
    this._numberError = ''
    this._disposeSearch?.()
    this.sendFromStore.reset()

    if (reset) {
      this.reset()
    }

    modalStore.removeModal(this._idModal)
  }

  init = async () => {
    try {
      this._loadingDeviceSettings = true

      const isAllowed = await this._entities.callStore.deviceSettingsStore.checkPermissions(true)

      if (isAllowed) {
        await this._entities.callStore.deviceSettingsStore.syncDevicesStream()

        this._entities.callStore.reactionSelectedAudioOutput()
        this._entities.callStore.reactionSelectedAudioInput()
        this.reactionSearch()
      } else {
        toastStore.add({
          type: 'error',
          title: 'Microphone access required',
          desc: 'To enable recording, please allow Salesmsg to access your microphone',
          action: {
            link: LINK_ENABLE_MICROPHONE,
            text: 'Learn more',
          },
        })
      }
    } catch (e) {
      logger.error(e)
    } finally {
      this._loadingDeviceSettings = false
    }
  }

  reset = () => {
    this._entities.callStore.deviceSettingsStore.removeStream()
    this._entities.callStore.stopStreamInputDeviceTrack()
  }

  handleClearValue = () => {
    this._contact = null
    this._contactId = null
    this._search = ''
  }

  handleChangeValue = (value: string, contact?: IContactAll | null) => {
    if (contact instanceof Contact) {
      this._contact = contact || null
      this._contactId = contact?.id || null
    }

    this._search = value
  }

  handleSubmit = async () => {
    try {
      this._loading = true
      this._numberError = ''

      if (!this._search) return

      if (this._search) {
        const isValidNumber = validPhoneNumber(this._search, this._contact?.countryCode)

        if (!isValidNumber) {
          this._numberError = 'Please enter a valid phone number'

          return
        }
      }

      if (!this._contactId) {
        try {
          const fromConversation = uiStore.pathName.includes('conversations')
          const isNoSearchInAllContacts =
            fromConversation && this._entities.subscriptionStore.isTrial
          let contact =
            !isNoSearchInAllContacts &&
            (await this._entities.contactsStore.getContactByNumber(this._search))

          if (!contact) {
            if (this._entities.userPermissionsStore.getItem('can_create_contacts')) {
              const params: IParamsCreateContact = {
                number: this._search,
                fromConversation,
              }

              contact = await this._entities.contactsStore.createContact(
                params,
                fromConversation
                  ? {
                      catchError: CreateContactErrors.trialLimit,
                    }
                  : undefined
              )
            } else {
              modalStore.setHideModalId(this._idModal)
              new FeatureRestrictedModalStore('Can create Contacts', () => {
                modalStore.removeHideModalId(this._idModal)
              })
            }
          }

          this._contactId = contact ? contact.id : null
          if (contact instanceof Contact) {
            this._contact = contact
          }
        } catch (e) {
          logger.error(e)
        }
      }

      if (!this.sendFromStore.inbox_id) {
        this.sendFromStore?.setError(true)

        return
      }

      if (this._contactId) {
        const contactForQuietHours =
          this._contact ?? this._entities.contactsStore.getItem(this._contactId)
        const timezone =
          contactForQuietHours instanceof Contact
            ? contactForQuietHours.timezone || contactForQuietHours.time_zone
            : ''
        const contactName =
          contactForQuietHours instanceof Contact
            ? contactForQuietHours.first_name || contactForQuietHours.last_name || 'this contact'
            : 'this contact'

        if (
          showCallDuringQuietHoursModalIfNeeded({
            timezone,
            contactName,
            organizationQuietHoursSettings: this._entities.organizationStore.quietHoursSettings,
            onConfirm: () => this.proceedWithCall(),
          })
        ) {
          this._loading = false
          return
        }

        await this.proceedWithCall()
      }
    } catch (e) {
      logger.error(e)
    } finally {
      this._loading = false
    }
  }

  proceedWithCall = async () => {
    if (!this._contactId || !this.sendFromStore.inbox_id) return

    this._loading = true
    try {
      const conversation = await this._entities.conversationStore.createConversation({
        contact_id: this._contactId,
        team_id: this.sendFromStore.inbox_id,
        number_id: this.sendFromStore.number_id,
        smart_option: this.sendFromStore.smart_option,
      })

      if (conversation instanceof Conversation) {
        this._config?.onSubmit?.(conversation.inbox_id)
        this.closeModal(false)
        await this._entities.callStore.connectTwilio(conversation.id)
      }
    } catch (e) {
      logger.error(e)
    } finally {
      this._loading = false
    }
  }

  reactionSearch = () => {
    this._disposeSearch?.()
    this._disposeSearch = reaction(
      () => this._search,
      (value) => {
        this._numberError = ''

        if (!value) {
          this._contact = null
        }
      }
    )
  }

  getIsAircallNumber = () => {
    return this.number?.is_aircall
  }

  get entities() {
    return this._entities
  }

  get disabledButton() {
    const isAircallNumber = this.getIsAircallNumber()
    const isOutOfMoney = this._entities.remindersStore.creditsStore.isOutOfMoney
    if (this._loading) return true
    if (isOutOfMoney && !isAircallNumber) return true
    if (this._entities.callStore.isBusy) return true
    if (this.isNumberNotVerifiedCallerId) return true

    return !this._search.length && !this._contact
  }

  get callButtonTooltip() {
    const isAircallNumber = this.getIsAircallNumber()
    const isOutOfMoney = this._entities.remindersStore.creditsStore.isOutOfMoney
    if (this.callerIdFailedReason) {
      return {
        label: this.callerIdFailedReason,
        fullWidth: true,
      }
    }

    return isOutOfMoney && !isAircallNumber
      ? {
          label: 'Your organization is out of message credits',
          fullWidth: true,
        }
      : undefined
  }

  get contact() {
    return this._contact
  }

  get numberError() {
    return this._numberError
  }

  get config() {
    return this._config
  }

  get inbox() {
    return this.sendFromStore.inbox
  }
  get number() {
    return this.sendFromStore.number
  }

  get isNumberNotVerifiedCallerId() {
    return this.inbox?.type === 'inbox' && this.inbox?.isNumberNotVerifiedCallerId
  }

  get callerIdFailedReason() {
    return this.inbox?.type === 'inbox' ? this.inbox?.callerIdFailedReason : null
  }
}
